import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import SignUp from './pages/SignUp';
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import LandingPage from './pages/LandingPage';
import LegalPage from './pages/LegalPage';
import HomeDesktop from './pages/HomeDesktop';
import HomeMobile from './pages/HomeMobile';
import FeedMobile from './pages/FeedMobile';
import ProfileMobile from './pages/ProfileMobile';
import ShowMobile from './pages/ShowMobile';
import MobileNavbar from './components/MobileNavbar';
import { MobileProvider } from './context/MobileContext';
import MobileRedirect from './components/MobileRedirect';
import { doc, getDoc } from 'firebase/firestore';

// Add global styles to hide scrollbars
const GlobalStyles = () => {
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Hide default scrollbar for the entire application */
      html, body {
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE and Edge */
        overflow-x: hidden;
      }
      
      html::-webkit-scrollbar, body::-webkit-scrollbar {
        display: none; /* Chrome, Safari, Opera */
        width: 0;
        background: transparent;
      }
      
      * {
        -webkit-overflow-scrolling: touch;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  return null;
};

const MobileLayout = ({ user }) => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('home');
  const [visibleTab, setVisibleTab] = useState('home');
  const mountedTabsRef = useRef({
    home: true,
    feed: false,
    profile: false
  });
  const [mountedTabs, setMountedTabs] = useState({
    home: true,
    feed: false,
    profile: false
  });
  
  // Set active tab based on current path
  useEffect(() => {
    if (location.pathname.includes('/feed')) {
      setActiveTab('feed');
      setVisibleTab('feed');
      // Mount the feed tab if not already mounted
      if (!mountedTabsRef.current.feed) {
        mountedTabsRef.current.feed = true;
        // Force re-render
        setMountedTabs({...mountedTabsRef.current});
      }
    } else if (location.pathname.includes('/profile')) {
      setActiveTab('profile');
      setVisibleTab('profile');
      // Mount the profile tab if not already mounted
      if (!mountedTabsRef.current.profile) {
        mountedTabsRef.current.profile = true;
        // Force re-render
        setMountedTabs({...mountedTabsRef.current});
      }
    } else if (location.pathname.includes('/show/')) {
      // Keep the active tab the same, but show the show page
      setVisibleTab('show');
    } else {
      setActiveTab('home');
      setVisibleTab('home');
    }
  }, [location]);
  
  return (
    <div style={{ 
      backgroundColor: '#131313',
      minHeight: '100vh',
      color: 'white',
      position: 'relative'
    }}>
      {/* Only mount tabs when they're first visited, then keep them mounted */}
      {mountedTabsRef.current.home && (
        <div style={{ display: visibleTab === 'home' ? 'block' : 'none' }}>
          <HomeMobile user={user} />
        </div>
      )}
      
      {mountedTabsRef.current.feed && (
        <div style={{ display: visibleTab === 'feed' ? 'block' : 'none' }}>
          <FeedMobile user={user} />
        </div>
      )}
      
      {mountedTabsRef.current.profile && (
        <div style={{ display: visibleTab === 'profile' ? 'block' : 'none' }}>
          <ProfileMobile user={user} />
        </div>
      )}
      
      {/* Show pages are rendered on top */}
      {visibleTab === 'show' && (
        <Routes>
          <Route path="/show/:showId/*" element={<ShowMobile user={user} />} />
        </Routes>
      )}
      
      <MobileNavbar activeTab={activeTab} />
    </div>
  );
};

const MainApp = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if the device is mobile
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
    };
    
    checkMobile();
    
    // Add resize listener to update if window size changes
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            setUserData(userDoc.data());
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        setUser(null);
        setUserData(null);
      }
      
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#1a1a1a'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          border: '3px solid #6600CC',
          borderTopColor: 'transparent',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Function to render protected routes
  const ProtectedRoute = ({ children }) => {
    if (!user) {
      return <Navigate to="/login" />;
    }
    return children;
  };

  return (
    <div style={{ 
      backgroundColor: '#131313',
      minHeight: '100vh',
      color: 'white'
    }}>
      <GlobalStyles />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={!user ? <LandingPage /> : <Navigate to="/home" />} />
        <Route path="/login" element={!user ? <SignUp /> : <Navigate to="/home" />} />
        <Route path="/legal" element={<LegalPage />} />
        
        {/* Protected routes */}
        {user && isMobile ? (
          <Route path="/*" element={
            <ProtectedRoute>
              <MobileRedirect user={user} />
            </ProtectedRoute>
          } />
        ) : (
          <>
            <Route path="/home" element={
              <ProtectedRoute>
                <HomeDesktop user={user} userData={userData} />
              </ProtectedRoute>
            } />
            
            {/* Add other desktop routes here */}
            
            {/* Redirect any other routes */}
            <Route path="*" element={<Navigate to={user ? "/home" : "/"} />} />
          </>
        )}
      </Routes>
      
      {user && !isMobile && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 100
        }}>
          <button 
            onClick={handleSignOut}
            style={{
              backgroundColor: '#6600CC',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              padding: '0.75rem 1.5rem',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

function App() {
  return (
    <Router>
      <MainApp />
    </Router>
  );
}

export default App;
